import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Row,
  Table as BTable,
  Modal,
  Spinner,
  Form,
  Dropdown,
} from "react-bootstrap";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { VideoItemControlsRedirectButton } from "./VideoItemControlsRedirectButton";
import EditableName from "@/components/Dashboard/EditableName";
import {
  PIPELINE_STAGE,
  STAGE_STATUSES_TEXT,
} from "../../constants/pipeline-stage";
import { PIPELINE_STATUS } from "../../constants/pipeline-status";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./Dashboard.scss";
import { matchApi, tasksApi, clientVideosApi } from "@/api";
import { toast } from "react-toastify";
import { Input } from "@headlessui/react";
import { cn, getTaskDisplayName } from "@/util/functions";
import {
  fetchDashboardMatchesList,
  continueVideoProcessing,
} from "@/store/dashboardSlice";
import Table from "@/components/Elements/Table";
import Tabs from "@/components/Elements/tabs";
import { PIPELINE_OPTIONS } from "@/constants";
import { Bind, UnBind, Square } from "@/components/Icons";

const DashboardContent = () => {
  const matchRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { matches, matchesCount } = useSelector(({ dashboard }) => dashboard);
  const [page, setPage] = useState(1);
  const [show, setShow] = useState(false);
  const [matchLink, setMatchLink] = useState("");
  const [isLoadingMatch, setIsLoadingMatch] = useState(false);
  const [pipelinePassing, setPipelinePassing] = useState(
    PIPELINE_OPTIONS[0].value
  );
  const [showTasksModal, setShowTasksModal] = useState(false);
  const [pipeConflictTasks, setPipeConflictTasks] = useState([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [showUploadCacheModal, setShowUploadCacheModal] = useState(false);
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [isUploadingCache, setIsUploadingCache] = useState(false);

  // New state for bind/unbind functionality
  const [showBindModal, setShowBindModal] = useState(false);
  const [showUnbindModal, setShowUnbindModal] = useState(false);
  const [selectedMatchForBinding, setSelectedMatchForBinding] = useState(null);
  const [selectedMatchForUnbinding, setSelectedMatchForUnbinding] =
    useState(null);
  const [orderNumber, setOrderNumber] = useState("");
  const [isBinding, setIsBinding] = useState(false);
  const [isUnbinding, setIsUnbinding] = useState(false);

  // Add new state for match search
  const [matchSearchQuery, setMatchSearchQuery] = useState("");
  const [isMatchSearching, setIsMatchSearching] = useState(false);
  const [reencodingMatchId, setReencodingMatchId] = useState(null);

  // Recalculate fitness modal
  const [showRecalcModal, setShowRecalcModal] = useState(false);
  const [recalcMatchId, setRecalcMatchId] = useState(null);
  const [recalcOptions, setRecalcOptions] = useState({
    use_kalman: false,
    distance_method: 'coast',
    use_scale_correction: false,
    use_homography: false,
    blend_margin: 0,
    fitness_only: true,
  });
  const [isRecalculating, setIsRecalculating] = useState(false);

  const columns = [
    {
      accessorKey: "id",
      header: "No",
    },
    {
      accessorKey: "order_id",
      header: "Order ID",
    },
    {
      accessorKey: "name",
      header: "Name",
      cell: (info) => (
        <EditableName
          value={info.getValue()}
          onChange={(value) => {
            matchApi
              .updateMatch(info.row.original.id, { name: value })
              .then((res) =>
                toast.success("Successfully changed the match name.")
              )
              .catch((err) =>
                toast.error("Something went wrong while changing the name.")
              );
          }}
        />
      ),
    },
    {
      accessorKey: "task",
      id: "Stage",
      header: "Stage",
      cell: (info) => {
        const { stage } = info.getValue();

        const stageText = STAGE_STATUSES_TEXT[stage];
        return (
          <>
            [{stage}]: {stageText}
          </>
        );
      },
    },
    {
      accessorKey: "task",
      id: "Task Status",
      header: "Task Status",
      cell: (info) => {
        const { status, stage } = info.getValue();

        const statusText =
          (status === PIPELINE_STATUS.pending && "Expectation") ||
          (status === PIPELINE_STATUS.started && (
            <span>
              Preparation <FontAwesomeIcon icon={["fas", "spinner"]} spin />
            </span>
          )) ||
          (status === PIPELINE_STATUS.progress && (
            <span>
              In progress <FontAwesomeIcon icon={["fas", "cog"]} spin />
            </span>
          )) ||
          (status === PIPELINE_STAGE.paused && "Paused") ||
          (status === PIPELINE_STATUS.success && "Success") ||
          (status === PIPELINE_STATUS.failure && "Failure") ||
          (status === PIPELINE_STATUS.retry && "Retry") ||
          (status === PIPELINE_STATUS.revoked && "Revoked");

        return (
          <>
            [{status}]: {statusText}{" "}
            {stage === "DETECTOR" && status === PIPELINE_STATUS.paused && (
              <Button
                variant="outline-success"
                onClick={() =>
                  info.table.options.meta.playPausePipeline(
                    info.row.original.id,
                    "proceed"
                  )
                }
              >
                <FontAwesomeIcon icon={["fas", "play"]} />
              </Button>
            )}
          </>
        );
      },
    },
    {
      accessorKey: "video",
      id: "Created",
      header: "Created",
      cell: (info) => {
        const video = info.getValue();
        return video?.created
          ? new Date(video.created).toLocaleDateString()
          : "-";
      },
    },
    {
      accessorKey: "video",
      id: "Duration",
      header: "Duration",
      cell: (info) => {
        const video = info.getValue();
        const duration =
          video?.duration &&
          new Date(video.duration * 1000).toISOString().substr(11, 8);
        return <>{duration || "-"}</>;
      },
    },
    {
      accessorKey: "video",
      id: "FPS",
      header: "FPS",
      cell: (info) => {
        const video = info.getValue();
        return video?.fps?.toFixed(2) || "-";
      },
    },
    {
      accessorKey: "task",
      id: "Markup",
      header: "Markup",
      cell: (info) => {
        const task = info.getValue();
        const { id, video } = info.row.original;
        return (
          <div>
            {task.status !== PIPELINE_STATUS.started && (
              <VideoItemControlsRedirectButton
                matchId={id}
                valueideo={video}
                task={task}
              />
            )}
            {task.stage === PIPELINE_STAGE.matchEvent && (
              <Button variant="outline-dark" onClick={() => openTasksModal(id)}>
                <FontAwesomeIcon icon={["fas", "list-check"]} size="sm" />
              </Button>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "pipe_conflicts",
      header: "Tasks",
      cell: (info) => {
        const { blocked_count, full_count } = info.getValue();
        return (
          <>
            {blocked_count}/{full_count}
          </>
        );
      },
    },
    {
      accessorKey: "task",
      header: "Progress",
      cell: (info) => {
        const { progress } = info.getValue();
        return <>{progress}%</>;
      },
    },
    {
      accessorKey: "tasks_done_flag",
      header: "Status",
      cell: (info) =>
        info.getValue() ? (
          <FontAwesomeIcon
            icon={["fas", "check-circle"]}
            className="mx-1"
            size="sm"
            color="green"
          />
        ) : (
          <FontAwesomeIcon
            icon={["fas", "times-circle"]}
            className="mx-1"
            size="sm"
          />
        ),
    },
    {
      accessorKey: "task",
      id: "Actions",
      header: "Actions",
      cell: (info) => {
        const task = info.getValue();
        const { video, id: matchId, order_id, pipeline_passing } = info.row.original;

        const STAGES_WITH_CONTINUE = [
          PIPELINE_STAGE.preparation,
          PIPELINE_STAGE.creationFinalJson,
          PIPELINE_STAGE.ballTouch,
          PIPELINE_STAGE.eventConflict,
          PIPELINE_STAGE.eventValidation,
          PIPELINE_STAGE.ballSupertrack,
          PIPELINE_STAGE.superTracks,
          PIPELINE_STAGE.eventTagging,
        ];


        const showContinue =
          STAGES_WITH_CONTINUE.includes(task.stage) &&
          task.status !== PIPELINE_STATUS.started;

        const isReencoding = reencodingMatchId === matchId;

        const allActions = [
          {
            key: "continue",
            label: <><FontAwesomeIcon icon={["fas", "play"]} /> Continue</>,
            action: async () => {
              const confirmed = window.confirm(
                "Are you sure you want to continue the pipeline?"
              );
              if (!confirmed) return;
              await dispatch(
                continueVideoProcessing({
                  videoId: video.pk,
                  currentStage: task.stage,
                })
              );
            },
            disabled: !showContinue || !task.is_ready,
          },
          {
            key: "bindUnbind",
            label:
              order_id === null ? (
                <>
                  <Bind width={14} height={14} color="currentColor" /> Bind
                </>
              ) : (
                <>
                  <UnBind width={14} height={14} color="currentColor" /> Unbind
                </>
              ),
            action: () =>
              order_id === null
                ? openBindModal(info.row.original)
                : openUnbindModal(info.row.original),
            disabled: false,
          },
          {
            key: "uploadCache",
            label: <><Square width={14} height={14} color="currentColor" /> Upload Cache</>,
            action: () => {
              setSelectedMatchId(matchId);
              setShowUploadCacheModal(true);
            },
            disabled: false,
          },
          {
            key: "verifyPositions",
            label: <><FontAwesomeIcon icon={["fas", "check-square"]} /> Verify Positions</>,
            action: () => navigate(`/panel/verify-positions/${matchId}`),
            disabled: !(
              task.stage === PIPELINE_STAGE.done &&
              task.status === PIPELINE_STATUS.success &&
              pipeline_passing === "team_analytics_pro"
            ),
          },
          {
            key: "recalcFitness",
            label: <><FontAwesomeIcon icon={["fas", "calculator"]} /> Recalculate Fitness</>,
            action: () => {
              setRecalcMatchId(matchId);
              setShowRecalcModal(true);
            },
            disabled: !(task.stage === PIPELINE_STAGE.done && task.status === PIPELINE_STATUS.success),
          },
          {
            key: "calibrateCamera",
            label: <><FontAwesomeIcon icon={["fas", "crosshairs"]} /> Calibrate Camera</>,
            action: () => navigate(`/panel/calibrate-camera/${matchId}`),
            disabled: !video?.pk,
          },
          {
            key: "reencode",
            label: isReencoding ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />{" "}
                Reencoding...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={["fas", "cogs"]} /> Reencode
              </>
            ),
            action: () => handleReencode(matchId),
            disabled: isReencoding,
          },
        ];

        return (
          <Dropdown drop="start">
            <Dropdown.Toggle
              variant="outline-secondary"
              size="sm"
              id={`action-dropdown-${matchId}`}
              disabled={isReencoding}
            >
              Actions
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
              {allActions.map((action) => (
                <Dropdown.Item
                  key={action.key}
                  onClick={action.action}
                  disabled={action.disabled}
                >
                  {action.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        );
      },
    },
  ];

  const handleBindMatch = async () => {
    if (!orderNumber.trim()) {
      toast.warning("Please enter an order number.");
      return;
    }

    try {
      setIsBinding(true);
      await matchApi.bindMatchToOrder(
        selectedMatchForBinding.id,
        orderNumber.trim()
      );
      toast.success("Successfully bound match to order.");
      setShowBindModal(false);
      setOrderNumber("");
      setSelectedMatchForBinding(null);
      // Refresh the dashboard data
      dispatch(
        fetchDashboardMatchesList({ page: page, search: matchSearchQuery })
      );
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        JSON.stringify(err.response?.data)?.slice(0, 200) ||
        "Something went wrong while binding match to order.";
      toast.error(errorMessage);
    } finally {
      setIsBinding(false);
    }
  };

  const handleUnbindMatch = async () => {
    try {
      setIsUnbinding(true);
      await matchApi.unbindMatchFromOrder(selectedMatchForUnbinding.id);
      toast.success("Successfully unbound match from order.");
      setShowUnbindModal(false);
      setSelectedMatchForUnbinding(null);
      // Refresh the dashboard data
      dispatch(
        fetchDashboardMatchesList({ page: page, search: matchSearchQuery })
      );
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        JSON.stringify(err.response?.data)?.slice(0, 200) ||
        "Something went wrong while unbinding match from order.";
      toast.error(errorMessage);
    } finally {
      setIsUnbinding(false);
    }
  };

  const handleRecalcFitness = async () => {
    try {
      setIsRecalculating(true);
      await matchApi.recalculateFitness(recalcMatchId, recalcOptions);
      toast.success(`Recalculation started for match ${recalcMatchId}`);
      setShowRecalcModal(false);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        JSON.stringify(err.response?.data)?.slice(0, 200) ||
        "Recalculation failed";
      toast.error(errorMessage);
    } finally {
      setIsRecalculating(false);
    }
  };

  const handleReencode = async (matchId) => {
    try {
      setReencodingMatchId(matchId);
      await clientVideosApi.reencodeVideo(matchId);
      toast.success("Match reencode initiated successfully");
      dispatch(
        fetchDashboardMatchesList({ page: page, search: matchSearchQuery })
      );
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        JSON.stringify(err.response?.data)?.slice(0, 200) ||
        "Something went wrong while reencoding match.";
      toast.error(errorMessage);
    } finally {
      setReencodingMatchId(null);
    }
  };

  const openBindModal = (match) => {
    setSelectedMatchForBinding(match);
    setShowBindModal(true);
  };

  const openUnbindModal = (match) => {
    setSelectedMatchForUnbinding(match);
    setShowUnbindModal(true);
  };

  const openTasksModal = async (matchId) => {
    try {
      setShowTasksModal(true);
      setIsLoadingTasks(true);
      const res = await matchApi.getPipeConflictTasks(matchId);
      const { results } = res;
      setPipeConflictTasks(results);
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          JSON.stringify(err.response?.data)?.slice(0, 200) ||
          "Something went wrong while fetching the tasks."
      );
    } finally {
      setIsLoadingTasks(false);
    }
  };

  const playPausePipeline = async (matchId, command) => {
    await tasksApi.playPausePiepeline(matchId, command);
  };

  const table = useReactTable({
    data: matches,
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      playPausePipeline,
    },
  });

  function fetchMoreMatches() {
    setPage(page + 1);
    dispatch(
      fetchDashboardMatchesList({ page: page + 1, search: matchSearchQuery })
    );
  }

  const handleMatchSearchChange = (e) => {
    setMatchSearchQuery(e.target.value);
  };

  const handleMatchSearch = async () => {
    setIsMatchSearching(true);
    try {
      setPage(1);
      dispatch(
        fetchDashboardMatchesList({ page: 1, search: matchSearchQuery })
      );
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          JSON.stringify(err.response?.data)?.slice(0, 200) ||
          err.message ||
          "Something went wrong while searching matches."
      );
    } finally {
      setIsMatchSearching(false);
    }
  };

  const handleClose = () => {
    setShow(false);
  };

  const handleSave = async () => {
    try {
      if (!matchLink) {
        matchRef.current?.focus();
        return toast.warning("Input a match link.");
      }
      setIsLoadingMatch(true);
      await matchApi.loadMatch(matchLink, pipelinePassing);
      toast.success("Sucessfully Loaded Match.");
      setShow(false);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        JSON.stringify(err.response?.data)?.slice(0, 200) ||
        "Something went wrong.";
      toast.error(errorMessage);
    } finally {
      setIsLoadingMatch(false);
    }
  };

  const handleUploadCache = async () => {
    try {
      setIsUploadingCache(true);
      await tasksApi.uploadCache(selectedMatchId);
      toast.success("Successfully uploaded cache.");
      setShowUploadCacheModal(false);
      // Refresh the dashboard data
      dispatch(
        fetchDashboardMatchesList({ page: page, search: matchSearchQuery })
      );
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        JSON.stringify(err.response?.data)?.slice(0, 200) ||
        "Something went wrong while uploading cache.";
      toast.error(errorMessage);
    } finally {
      setIsUploadingCache(false);
    }
  };

  return (
    <div className="dashboard">
      <div className="match-block">
        <div className="tw-flex tw-justify-between tw-mb-2">
          <h3 className="">Recent</h3>

          <div className="tw-flex tw-items-center tw-gap-2">
            <div className="tw-flex tw-items-center tw-gap-2">
              <Form.Control
                type="text"
                placeholder="Search by Match ID or Order ID..."
                value={matchSearchQuery}
                onChange={handleMatchSearchChange}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleMatchSearch();
                  }
                }}
                className="tw-w-64"
              />
              <Button
                variant="outline-secondary"
                onClick={handleMatchSearch}
                disabled={isMatchSearching}
                className="tw-px-4"
              >
                {isMatchSearching ? (
                  <>
                    <Spinner animation="border" size="sm" className="tw-mr-2" />
                    Filtering...
                  </>
                ) : (
                  "Filter"
                )}
              </Button>
            </div>

            <Button
              variant="success"
              onClick={() => {
                setShow(true);
              }}
            >
              Load Match
            </Button>
          </div>
        </div>
        {matches.length ? (
          <Table table={table} />
        ) : (
          <span>
            No videos uploaded. Please use the form above to upload a video.
          </span>
        )}
      </div>
      {matches.length < matchesCount && (
        <div className="tw-w-full tw-flex tw-justify-center">
          <div className="tw-max-w-[400px] tw-w-full">
            <Button className="tw-w-full" onClick={() => fetchMoreMatches()}>
              Load more
            </Button>
          </div>
        </div>
      )}

      {/* Bind Modal */}
      <Modal show={showBindModal} onHide={() => setShowBindModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Bind Match({selectedMatchForBinding?.id}) to Order
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Please provide an order number to bind:</Form.Label>
            <Form.Control
              type="number"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              placeholder="Enter order number"
              autoFocus
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleBindMatch();
                }
              }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBindModal(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleBindMatch}
            disabled={isBinding}
          >
            {isBinding ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Binding...
              </>
            ) : (
              "OK"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Unbind Modal */}
      <Modal show={showUnbindModal} onHide={() => setShowUnbindModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Unbind Match({selectedMatchForUnbinding?.id}) from Order
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Do you really want to unbind this match from the order{" "}
            <strong>{selectedMatchForUnbinding?.order_id}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUnbindModal(false)}>
            Cancel
          </Button>
          <Button
            className="tw-rounded-md"
            variant="danger"
            onClick={handleUnbindMatch}
            disabled={isUnbinding}
          >
            {isUnbinding ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Unbinding...
              </>
            ) : (
              "OK"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Existing modals */}
      <Modal
        show={show}
        onHide={handleClose}
        dialogClassName="tw-max-w-[800px] tw-w-full"
      >
        <Modal.Header closeButton>
          <Modal.Title>Load Match</Modal.Title>
        </Modal.Header>
        <Modal.Body className="tw-flex tw-flex-col tw-gap-4 tw-px-6 tw-py-6">
          <Form.Select
            className="tw-w-fit"
            value={pipelinePassing}
            onChange={(e) => setPipelinePassing(e.target.value)}
          >
            {PIPELINE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
          <Input
            ref={matchRef}
            className={cn(
              "tw-border tw-block tw-w-full tw-rounded-lg tw-py-1.5 tw-px-3 tw-text-md"
            )}
            placeholder="Input a match url."
            onChange={(e) => setMatchLink(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button
            variant="success"
            onClick={handleSave}
            disabled={isLoadingMatch}
          >
            {isLoadingMatch ? (
              <>
                Loading{" "}
                <Spinner
                  animation="border"
                  role="status"
                  variant="light"
                  size="sm"
                >
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showTasksModal} onHide={() => setShowTasksModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Match Tasks</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoadingTasks ? (
            <div className="tw-flex tw-justify-center">
              <Spinner animation="border" role="status" size="sm"></Spinner>
            </div>
          ) : (
            <div className="tw-flex tw-flex-col tw-gap-2">
              {pipeConflictTasks.length === 0 ? (
                <div className="tw-text-center tw-text-gray-500">
                  There's no task for this match
                </div>
              ) : (
                pipeConflictTasks.map((task) => (
                  <div
                    key={task.id}
                    className="tw-flex tw-justify-between tw-gap-4 tw-items-center"
                  >
                    <div>{task.id}</div>
                    <div>{getTaskDisplayName(task)}</div>
                    {task.resolved_at ? (
                      <span className="tw-bg-green-100 tw-text-green-800 tw-px-2 tw-py-1 tw-rounded-md tw-text-xs tw-font-medium">
                        COMPLETED
                      </span>
                    ) : (
                      <Button
                        className="tw-px-3"
                        variant="primary"
                        onClick={() => {
                          const result = window.confirm(
                            `Are you sure you want to complete this task?`
                          );

                          if (result) {
                            setIsLoadingTasks(true);
                            matchApi
                              .postMatchTaskResult({
                                matchId: task.match,
                                taskId: task.id,
                                taskResult: {
                                  status: true,
                                },
                                taskType: task.task_type, // Dynamically use the task's type
                              })
                              .then(() => {
                                toast.success("Task marked as completed");
                                openTasksModal(task.match); // Refresh the tasks list
                              })
                              .catch((err) => {
                                const errorMessage =
                                  err.response?.data?.message ||
                                  JSON.stringify(err.response?.data)?.slice(
                                    0,
                                    200
                                  ) ||
                                  "Failed to complete task";
                                toast.error(errorMessage);
                              })
                              .finally(() => {
                                setIsLoadingTasks(false);
                              });
                          }
                        }}
                        size="sm"
                      >
                        COMPLETE
                      </Button>
                    )}
                    <Button
                      className="tw-px-3"
                      variant="secondary"
                      onClick={() => {
                        navigate(
                          `/panel/event-light/${task.match}?taskId=${task.id}`
                        );
                      }}
                      size="sm"
                    >
                      <FontAwesomeIcon
                        icon={["fas", "hand-point-right"]}
                        size="sm"
                      />
                    </Button>
                  </div>
                ))
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showUploadCacheModal}
        onHide={() => setShowUploadCacheModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Upload Cache Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Do you really want to restore data (boxes) from cache?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowUploadCacheModal(false)}
          >
            No
          </Button>
          <Button
            variant="success"
            onClick={handleUploadCache}
            disabled={isUploadingCache}
          >
            {isUploadingCache ? (
              <>
                Uploading{" "}
                <Spinner
                  animation="border"
                  role="status"
                  variant="light"
                  size="sm"
                >
                  <span className="visually-hidden">Uploading...</span>
                </Spinner>
              </>
            ) : (
              "Yes"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Recalculate Fitness Modal */}
      <Modal show={showRecalcModal} onHide={() => setShowRecalcModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Recalculate Fitness — Match {recalcMatchId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <small className="text-muted d-block mb-2">Сглаживание координат</small>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="recalc-kalman"
                label="Kalman filter (сглаживание bbox в пиксельном пространстве)"
                checked={recalcOptions.use_kalman}
                onChange={(e) => setRecalcOptions(prev => ({...prev, use_kalman: e.target.checked}))}
              />
              <Form.Text className="text-muted">
                Выкл — PCA сглаживание в мировых координатах (legacy).
              </Form.Text>
            </Form.Group>

            <small className="text-muted d-block mb-2">Расчёт дистанции</small>
            <Form.Group className="mb-3">
              <Form.Label>Distance Method</Form.Label>
              <Form.Select
                value={recalcOptions.distance_method}
                onChange={(e) => setRecalcOptions(prev => ({...prev, distance_method: e.target.value}))}
              >
                <option value="frame">Frame-by-frame (покадровый, фильтр 0.3-12 м/с)</option>
                <option value="coast">Coast-distance (накопление &gt; 2.5м, legacy)</option>
              </Form.Select>
            </Form.Group>

            <small className="text-muted d-block mb-2">Коррекция дисторсии</small>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="recalc-scale"
                label="Scale correction (коррекция из штанг/штрафных/вратарских)"
                checked={recalcOptions.use_scale_correction}
                onChange={(e) => setRecalcOptions(prev => ({...prev, use_scale_correction: e.target.checked}))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="recalc-homography"
                label="Homography (findHomography из всех точек поля + зон)"
                checked={recalcOptions.use_homography}
                onChange={(e) => setRecalcOptions(prev => ({...prev, use_homography: e.target.checked}))}
              />
              <Form.Text className="text-muted">
                Заменяет 4-квадрантный transform + scale correction.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Blend margin (px, 0 = выкл)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                max="200"
                value={recalcOptions.blend_margin}
                onChange={(e) => setRecalcOptions(prev => ({...prev, blend_margin: parseInt(e.target.value) || 0}))}
              />
              <Form.Text className="text-muted">
                Зона смешивания на границах квадрантов (рекомендуется 50 при включении).
              </Form.Text>
            </Form.Group>

            <hr />
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="recalc-fitness-only"
                label="Только фитнес (пропустить events, heatmaps, positions)"
                checked={recalcOptions.fitness_only}
                onChange={(e) => setRecalcOptions(prev => ({...prev, fitness_only: e.target.checked}))}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRecalcModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleRecalcFitness}
            disabled={isRecalculating}
          >
            {isRecalculating ? (
              <><Spinner size="sm" animation="border" /> Recalculating...</>
            ) : (
              "Recalculate"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DashboardContent;
